import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { conversationsForUserState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import { getConversationLabel, type HomeId, isConversationReadOnly, type UserId } from '@rock/types';
import { type FC, useMemo } from 'react';

/**
 * Chat conversations about this home. Conversations live under the OWNER's user doc
 * (users/{owner}/chat-conversations, homeId field on each) — there's no per-home collection and no
 * cross-user query path, so this reads the owner's recent conversations and filters client-side.
 * The transcript viewer lives on the user detail page; rows link there.
 */
export const HomeChatsTab: FC<{ homeId: HomeId; ownerId: UserId }> = ({ homeId, ownerId }) => {
    const [conversations, conversationsLoadable] = useLoadableAtomValue(conversationsForUserState(ownerId), []);
    const homeConversations = useMemo(
        () => conversations.filter((conversation) => conversation.homeId === homeId),
        [conversations, homeId],
    );
    const onRowClick = useRowClick();

    return (
        <Card>
            <CardHeader>
                <CardTitle>Chats</CardTitle>
                <CardDescription>
                    The owner's chat conversations about this home (from their {conversations.length} most recent). Rows
                    open the full transcript on the user page.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {homeConversations.length === 0 ? (
                    <div className="section-description">
                        {conversationsLoadable.state === 'loading'
                            ? 'Loading conversations…'
                            : conversationsLoadable.state === 'hasError'
                              ? 'Couldn’t load conversations.'
                              : 'No conversations about this home yet.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Conversation</TableHead>
                                <TableHead className="text-right">Messages</TableHead>
                                <TableHead className="text-right">Est. cost</TableHead>
                                <TableHead className="text-right">Updated</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {homeConversations.map((conversation) => (
                                <TableRow
                                    key={conversation.id}
                                    className="cursor-pointer transition-colors hover:bg-muted/50"
                                    onClick={onRowClick(
                                        `/directory/users/${ownerId}/chats/${encodeURIComponent(conversation.id)}`,
                                    )}
                                >
                                    <TableCell className="max-w-[360px] font-medium">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate">{getConversationLabel(conversation)}</span>
                                            {isConversationReadOnly(conversation) && (
                                                <Badge variant="outline">Read-only</Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">{conversation.messages.length}</TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        ${conversation.estimatedCostUsd.toFixed(3)}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(conversation.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
